<template>
  <div class="row poppins h-100">
    <div class="col-12 h-100">
      <div class="table-wrapper h-100 d-flex flex-column">
        <div class="filter-block">
          <SearchInput :search-change="searchChange" />
          <div class="d-flex">
            <b-button v-if="isSuperadmin" v-b-modal.add-company-modal variant="primary" size="lg" class="pl-3 pr-4 d-flex align-items-center">
              <BIconPlus class="mr-2" />
              {{ 'Add Company' | i18n }}
            </b-button>
            <UpsertCompany id="add-company-modal" :get-page="getPage" title="New Company" :on-save="(d) => api.commonCompany.create(d)" />
            <UpsertCompany
              id="edit-company-modal"
              :get-page="getPage"
              title="Edit Company"
              :init-data="selectedCompany"
              :on-save="(d, id) => api.commonCompany.update(d, id)"
              type="edit"
            />
            <b-button
              v-if="isSuperadmin"
              variant="outline-danger"
              size="lg"
              class="ml-3 pl-4 pr-4 d-flex align-items-center"
              :disabled="!selectedList.length"
              @click="deleteCompany"
            >
              <img :src="deleteIconUrl" class="mr-2" />
              {{ 'Remove Company' | i18n }}
            </b-button>
          </div>
        </div>
        <Table
          :update="getPage"
          :columns="columns"
          :rows="rows"
          :filter="filter"
          :update-filter="updateFilter"
          :loading-instance="loadingInstance"
          :set-loading-instance="setLoadingInstance"
          :selected-list="selectedList"
          :set-selected-list="setSelectedList"
          class="overflow-auto pr-1"
          tableName="oneCompanyTable"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { BIconPlus, BIconPencil } from 'bootstrap-vue';
import api from '../../../../api';

import { DeleteRowMixin, NotificationMixin, AuthMixin } from '../../../../../mixins';
import PageHeader from '../../../common/page-header';
import Table from '../../../common/table';
import SearchInput from '../../../common/search-input';
import DeleteIcon from '../../../../../assets/icons/delete.svg';

import UpsertCompany from './upsert-company';

let inputInterval = null;

export default {
  title: 'Company',
  components: { PageHeader, BIconPlus, BIconPencil, Table, UpsertCompany, SearchInput, DeleteIcon },
  mixins: [DeleteRowMixin, NotificationMixin, AuthMixin],
  data() {
    return {
      deleteIconUrl: DeleteIcon,
      api: api,
      selectedList: [],
      selectedCompany: {},
      loadingInstance: null,
      filter: {
        page: 1,
        limit: 10,
        totalCount: 0,
        sortBy: '',
        sortOrder: '',
        searchQuery: '',
      },
      columns: [
        {
          field: 'check',
          key: '1',
          type: 'checkbox',
          title: '',
          width: 50,
          align: 'center',
        },
        {
          field: 'name',
          title: this.$translate('Name'),
          key: '2',
          sortBy: '',
          align: 'left',
        },
        {
          field: 'address',
          title: this.$translate('Company address'),
          key: '3',
          sortBy: '',
          align: 'left',
        },
        {
          field: 'actions',
          title: '',
          key: '100',
          renderBodyCell: ({ row }) => {
            return this.isSuperadmin
              ? (
                <div class="action-block">
                  <div class="details-action" on-click={() => this.editCompany(row)}>
                    <BIconPencil />
                  </div>
                </div>
              )
              : null;
          },
        },
      ],
      rows: [],
    };
  },
  methods: {
    editCompany(company) {
      this.selectedCompany = company;
      setTimeout(() => {
        this.$root.$emit('bv::show::modal', 'edit-company-modal');
      }, 100);
    },
    updateFilter(f) {
      this.filter = {
        ...this.filter,
        ...f,
      };
    },
    setSelectedList(list) {
      this.selectedList = list;
    },
    setLoadingInstance(loadingInstance) {
      this.loadingInstance = loadingInstance;
    },
    searchChange(e) {
      const v = e.target.value;
      this.filter.searchQuery = v;
      if (inputInterval) {
        clearTimeout(inputInterval);
      }
      inputInterval = setTimeout(() => {
        this.getPage();
      }, 500);
    },
    async deleteCompany() {
      try {
        await this.deleteSelectedRows({}, async () => {
          const ids = this.selectedList.map((r) => r.id);
          await api.commonCompany.delete({ ids });
          this.rows = this.rows.filter((i) => !ids.includes(i.id));
          this.selectedList = [];
          this.getPage();
        });
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
    startLoading() {
      this.loadingInstance.show();
    },
    stopLoading() {
      this.loadingInstance.close();
    },
    async getPage() {
      try {
        this.startLoading();
        const { page, limit, sortBy, sortOrder, searchQuery } = this.filter;
        const ctx = {
          page,
          limit,
          sortBy,
          sortOrder,
          searchQuery,
        };
        const response = await api.commonCompany.get(ctx);
        this.filter.totalCount = response.totalCount;
        this.stopLoading();
        this.rows = response.data.map((i) => ({ ...i, articleName: i.article ? i.article.articleName1 : '' }));
      } catch (err) {
        this.showNotification({ type: 'error', message: err.message });
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .ve-table-body {
  ::v-deep th {
    color: rgb(112, 112, 112) !important;
    background-color: rgb(239, 239, 239) !important;
    border: none !important;
    min-height: 50px;
  }
  ::v-deep td {
    color: rgb(112, 112, 112) !important;
    cursor: pointer;
  }
}

::v-deep .ve-table-cell-selection {
  border: none !important;
}

::v-deep .ve-table-sort {
  top: -4.5px;
}

::v-deep .details-action {
  width: 35px;
  height: 35px;
  border-radius: 50%;
  background: rgb(34, 129, 254);
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  font-size: 20px;
  margin: auto;
}

.statuses-block {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.date-range-block {
  min-width: 350px;
  text-align: center;
  margin-left: 30px;
  display: flex;
  align-items: center;
}

::v-deep .ve-select-selected-span {
  font-weight: 400;
}

.pagination-block {
  display: flex;
  justify-content: flex-end;
  margin-top: 30px;
}

::v-deep .copy-button {
  background: rgb(237, 248, 254);
  border: 2px solid rgb(175, 216, 255);
  border-radius: 4px;
  padding: 2px 13px;
  margin: 0px 5px;
  color: rgb(49, 141, 255);
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  margin-right: 20px;
}

.action-block {
  display: flex;
}

::v-deep .btn-lg {
  font-size: 16px;
  line-height: 24px;
}
</style>
